'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import { useParams } from 'next/navigation';
import type { Folder } from '@frameio/graphql-client/generated';
import { ensureString } from '@frameio/utils/formatters';

type FolderId = Folder['id'] | undefined;

export const IsolatedActiveFolderIdContext = createContext<FolderId>(undefined);

export const useActiveFolderId = () =>
  useContext(IsolatedActiveFolderIdContext);

type IsolatedActiveFolderContextProviderProps = {
  children: React.ReactNode;
  folderId?: FolderId;
};

export const IsolatedActiveFolderIdContextProvider = ({
  folderId,
  children,
}: IsolatedActiveFolderContextProviderProps) => {
  return (
    <IsolatedActiveFolderIdContext.Provider value={folderId}>
      {children}
    </IsolatedActiveFolderIdContext.Provider>
  );
};

/**
 * Helper context to derive the active folder id from the route or fallback to
 * the project root folder id.
 */
export const ActiveFolderIdContext = (
  props: PropsWithChildren<{
    folderId?: string;
  }>
) => {
  const params = useParams();
  let folderAssetId = props.folderId;

  // If the route has a folder id, then that is the active folder
  if ('folderAssetId' in params && params.folderAssetId) {
    folderAssetId = ensureString(params.folderAssetId);
  }

  return (
    <IsolatedActiveFolderIdContextProvider folderId={folderAssetId}>
      {props.children}
    </IsolatedActiveFolderIdContextProvider>
  );
};
